import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { makeVocab } from "@/lib/vocab";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  MessagesSquare, Phone, Wrench, CalendarCheck2, CalendarDays, Vote, Megaphone,
  Headset, FileText, MessageCircle, Link2, Copy, Printer, Download, Smartphone, QrCode,
} from "lucide-react";

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const fmtPhone = (p) => (p || "").replace(/^\+1(\d{3})(\d{3})(\d{4})$/, "($1) $2-$3") || p;

/* Build the capability list from what's actually switched ON for this community right now.
   Same gates the pipeline enforces — the flyer never promises something the concierge won't do.
   VOICE-FIRST: when the voice concierge is on, calling leads and texting follows. */
export function enabledCapabilities(t) {
  const feats = t.features || {};
  const ents = t.entitlements || {};
  const acts = t.enabled_actions || {};
  const textOn = t.text_enabled !== false && t.concierge_enabled !== false;
  const voiceOn = !!t.voice_ai_enabled && t.voice_enabled !== false;
  const caps = [];
  if (voiceOn)
    caps.push({ id: "voice", icon: Phone, title: "Call and just talk", desc: "A friendly voice answers 24/7 — no menus, no hold music, real help right away." });
  if (textOn)
    caps.push({ id: "text", icon: MessagesSquare, title: voiceOn ? "Or text your question" : "Instant answers by text", desc: "Rules, amenities, hours, HOA questions — instant answers any time of day." });
  if (t.mode !== "answers_only" && feats.requests !== false && acts.maintenance !== false)
    caps.push({ id: "requests", icon: Wrench, title: "Maintenance & requests", desc: "Report an issue in one call or text — you'll get a reference number and status updates." });
  if (t.amenities_enabled && (ents.amenities ?? true))
    caps.push({ id: "amenities", icon: CalendarCheck2, title: "Amenity reservations", desc: "Book community amenities straight from your phone." });
  if (feats.events && acts.rsvp !== false)
    caps.push({ id: "events", icon: CalendarDays, title: "Events & RSVPs", desc: "Hear what's coming up and RSVP with a single text." });
  if (t.surveys_enabled && (ents.surveys ?? true))
    caps.push({ id: "surveys", icon: Vote, title: "Community surveys", desc: "Have your say — cast votes by text when the community asks." });
  if (t.broadcasts_enabled && (ents.broadcasts ?? true))
    caps.push({ id: "alerts", icon: Megaphone, title: "Community alerts", desc: "Time-sensitive announcements delivered the moment they go out." });
  if (feats.knowledge !== false && textOn)
    caps.push({ id: "documents", icon: FileText, title: "Official documents", desc: "Ask for bylaws, forms or meeting minutes — sent right to your phone." });
  if ((t.support_module || {}).enabled)
    caps.push({ id: "person", icon: Headset, title: "A real person when you need one", desc: "Say \u201ctalk to a person\u201d and the community team takes over the conversation." });
  if (t.whatsapp_enabled && (ents.whatsapp ?? false))
    caps.push({ id: "whatsapp", icon: MessageCircle, title: "Also on WhatsApp", desc: "Prefer WhatsApp? Message the same number there." });
  return caps;
}

/* Community logo, normalized so ANY shape prints well: fixed-footprint white chip, the image
   letterboxed inside with object-contain (never stretched/cropped), hidden entirely if the
   file is missing or fails to load — the flyer looks intentional with or without a logo. */
function LogoChip({ src, testId }) {
  const [ok, setOk] = useState(true);
  if (!src || !ok) return null;
  return (
    <div data-testid={testId} className="shrink-0 h-16 w-[8.5rem] rounded-xl border border-sand-200 bg-white flex items-center justify-center px-3 py-2 overflow-hidden">
      <img src={src} alt="Community logo" onError={() => setOk(false)}
        className="max-h-full max-w-full object-contain" />
    </div>
  );
}

/* One full US-Letter onboarding page. Rendered twice: as the on-screen preview inside the
   dialog, and as a print-only copy portaled to <body> (Radix dialogs live inside a transformed
   container, which breaks position:fixed print layouts — a direct body child doesn't). */
function FlyerPage({ t, qrId, testPrefix = "", beta = false, headline = "", welcome = "", whiteLabel = false }) {
  const vt = makeVocab(t);            // this community's own words — the list can span industries
  const url = `${window.location.origin}/opt-in/${slugify(t.name)}`;
  const persona = t.persona_name || `Ask ${t.name}`;
  const phone = fmtPhone(t.phone_number);
  const voiceOn = !!t.voice_ai_enabled && t.voice_enabled !== false;
  const brand = (t.public_site || {}).brand_color || (t.rcs_branding || {}).color || "#C8102E";
  const caps = enabledCapabilities(t);
  const contactWord = voiceOn ? "Call or text" : "Text";
  const tid = (s) => (testPrefix ? undefined : s); // testids only on the visible preview copy
  const logoPath = (t.public_site || {}).logo_path || t.report_logo_path || (t.rcs_branding || {}).logo_path || "";
  const logoSrc = logoPath ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${logoPath}` : "";
  const title = (headline || "").trim() || persona;
  const welcomeLine = (welcome || "").trim()
    || `${t.name}\u2019s always-on assistant. One number for the whole community — ${voiceOn ? "call it or text it, day or night, and get real help in seconds." : "text it day or night and get real help in seconds."}`;

  return (
    <>
      {/* Header */}
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-[0.3em] font-bold" style={{ color: brand }}>
            Meet your community concierge
          </div>
          <h1 data-testid={tid("invite-flyer-title")} className="font-heading text-4xl font-extrabold tracking-tight mt-2 leading-none break-words text-ink">
            {title}
            {beta && (
              <span data-testid={tid("invite-flyer-beta")}
                className="ml-3 inline-block align-[0.45em] text-[10px] uppercase tracking-[0.18em] font-bold rounded-full border-2 px-2.5 py-0.5 whitespace-nowrap"
                style={{ color: brand, borderColor: brand }}>
                In beta
              </span>
            )}
          </h1>
        </div>
        <LogoChip src={logoSrc} testId={tid("invite-flyer-logo")} />
      </div>
      <p data-testid={tid("invite-flyer-welcome")} className="text-ink-700 mt-2 text-[15px] leading-snug max-w-xl">
        {welcomeLine}
      </p>

      {/* Phone band */}
      {t.phone_number ? (
        <div data-testid={tid("invite-flyer-phone")} className="mt-5 rounded-2xl px-6 py-4 flex items-center justify-between gap-4 text-white" style={{ backgroundColor: brand }}>
          <div className="flex items-center gap-3 min-w-0">
            <span className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center shrink-0">
              {voiceOn ? <Phone className="h-5 w-5" strokeWidth={2} /> : <Smartphone className="h-5 w-5" strokeWidth={2} />}
            </span>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-white/80">{contactWord} anytime</div>
              <div className="font-heading text-3xl font-extrabold tracking-tight leading-none mt-0.5">{phone}</div>
            </div>
          </div>
          <div className="text-right text-[11px] leading-tight text-white/85 shrink-0">
            Save it in your contacts<br />as <span className="font-bold text-white">&ldquo;{persona}&rdquo;</span>
          </div>
        </div>
      ) : (
        <div className="mt-5 rounded-2xl px-6 py-4 bg-sand-50 border border-sand-200 text-sm text-ink-600">
          Your community number is being set up — opt in below and you&rsquo;ll be first to hear when it&rsquo;s live.
        </div>
      )}

      {/* Body: capabilities + QR */}
      <div className="mt-6 grid grid-cols-[1fr_auto] gap-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-ink/40">What can I help with?</div>
          <div data-testid={tid("invite-flyer-capabilities")} className="mt-3 space-y-3">
            {caps.map((c) => (
              <div key={c.id} data-testid={tid(`invite-cap-${c.id}`)} className="flex items-start gap-3">
                <span className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: `${brand}14` }}>
                  <c.icon className="h-3.5 w-3.5" strokeWidth={2} style={{ color: brand }} />
                </span>
                <div className="min-w-0">
                  <div className="text-[13px] font-bold text-ink leading-tight">{c.title}</div>
                  <div className="text-[12px] text-ink-600 leading-snug mt-0.5">{c.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="w-[200px] shrink-0 text-center">
          <div className="rounded-2xl border border-sand-200 bg-white p-3 inline-block">
            <QRCodeCanvas id={qrId} value={url} size={168} level="M" includeMargin fgColor="#141417" />
          </div>
          <div className="mt-2 text-[11px] uppercase tracking-[0.2em] font-bold inline-flex items-center gap-1 justify-center" style={{ color: brand }}>
            <QrCode className="h-3 w-3" /> Get started
          </div>
          <ol className="text-left text-[12px] text-ink-800 space-y-1.5 mt-2">
            {voiceOn && t.phone_number ? (
              <>
                <li><span className="font-bold" style={{ color: brand }}>1.</span> Call <span className="font-bold">{phone}</span> and just start talking — or text <span className="font-bold">HELLO</span>.</li>
                <li><span className="font-bold" style={{ color: brand }}>2.</span> Or scan the code with your phone camera &amp; opt in with your mobile number.</li>
                <li><span className="font-bold" style={{ color: brand }}>3.</span> Ask your first question — that&rsquo;s it.</li>
              </>
            ) : (
              <>
                <li><span className="font-bold" style={{ color: brand }}>1.</span> Scan the code with your phone camera &amp; opt in with your mobile number.</li>
                {t.phone_number && (
                  <li><span className="font-bold" style={{ color: brand }}>2.</span> Or just text <span className="font-bold">HELLO</span> to {phone}.</li>
                )}
                <li><span className="font-bold" style={{ color: brand }}>{t.phone_number ? "3." : "2."}</span> Ask your first question — that&rsquo;s it.</li>
              </>
            )}
          </ol>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-sand-200">
        {(t.public_site || {}).mgmt_name ? (
          <p className="text-[11px] font-semibold text-ink-600">Provided for {t.name} {vt.lcs("person")} by {(t.public_site || {}).mgmt_name}.</p>
        ) : null}
        <p className="text-[10px] text-ink-400 mt-1 leading-relaxed">
          By opting in you agree to receive automated messages from your community. Msg frequency varies.
          Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help. For emergencies, always call 911.
          {!whiteLabel && " Powered by Ask Redline."}
        </p>
      </div>
    </>
  );
}

/* Full-page (US Letter) printable onboarding flyer for residents. The dialog shows a live
   preview; Print / Save PDF outputs exactly one branded page via the print-only body portal. */
export function InviteFlyerDialog({ community, onClose }) {
  const [beta, setBeta] = useState(false);
  const [whiteLabel, setWhiteLabel] = useState(false);
  const [headline, setHeadline] = useState("");
  const [welcome, setWelcome] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setBeta(false);
    setWhiteLabel(false);
    setHeadline((community?.invite_flyer || {}).headline || "");
    setWelcome((community?.invite_flyer || {}).welcome || "");
  }, [community?.id]);
  if (!community) return null;
  const t = community;
  const vt = makeVocab(community);
  const url = `${window.location.origin}/opt-in/${slugify(t.name)}`;
  const persona = t.persona_name || `Ask ${t.name}`;

  const saveDefaults = async () => {
    setSaving(true);
    try {
      const fl = { headline: headline.trim(), welcome: welcome.trim() };
      await api.put(`/tenants/${t.id}/config`, { invite_flyer: fl });
      t.invite_flyer = fl; // keep the in-memory row in sync for reopen
      toast.success("Saved — this community's flyer will remember your wording");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const downloadPng = () => {
    const canvas = document.getElementById("invite-qr-canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `${slugify(t.name)}-optin-qr.png`;
    a.click();
  };

  return (
    <Dialog open={!!community} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Invite {vt.lcs("person")}</DialogTitle></DialogHeader>
        <p className="text-sm text-ink-600 -mt-1">
          A ready-to-print onboarding page for {t.name} — built from the services you have switched on right now.
          Print it, save it as a PDF, or drop it in mailboxes, the clubhouse and welcome packets.
        </p>

        <div className="flex items-center gap-2 rounded-xl border border-sand-200 bg-sand-50 px-3 py-2">
          <Link2 className="h-4 w-4 text-ink-400 shrink-0" />
          <span className="text-sm text-ink-700 truncate flex-1 min-w-0">{url}</span>
          <button data-testid="invite-copy-link" onClick={() => { navigator.clipboard.writeText(url); toast.success("Opt-in link copied"); }}
            className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-ink rounded-full px-3 py-1.5 hover:bg-ink-700 transition-colors shrink-0">
            <Copy className="h-3 w-3" /> Copy
          </button>
        </div>

        <style>{`
          #invite-doc-print { display: none; }
          @media print {
            @page { size: letter; margin: 0; }
            body > *:not(#invite-doc-print) { display: none !important; }
            #invite-doc-print { display: flex !important; flex-direction: column; width: 100%; height: 100vh;
              background: #fff; padding: 0.55in 0.65in; }
            #invite-doc-print > div:last-child { margin-top: auto; }
            #invite-doc-print * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        `}</style>

        <div className="flex flex-col sm:flex-row gap-2">
          <label data-testid="invite-beta-toggle" className="flex items-center gap-2 rounded-xl border border-sand-200 bg-white px-3 py-2 cursor-pointer hover:border-amber-300 transition-colors">
            <input type="checkbox" checked={beta} onChange={(e) => setBeta(e.target.checked)}
              className="h-4 w-4 accent-[#C8102E]" data-testid="invite-beta-check" />
            <span className="text-xs text-ink-700"><span className="font-bold text-ink">Add an &ldquo;In beta&rdquo; label</span> — shows a small badge next to the title</span>
          </label>
          <label data-testid="invite-whitelabel-toggle" className="flex items-center gap-2 rounded-xl border border-sand-200 bg-white px-3 py-2 cursor-pointer hover:border-ink/30 transition-colors">
            <input type="checkbox" checked={whiteLabel} onChange={(e) => setWhiteLabel(e.target.checked)}
              className="h-4 w-4 accent-[#C8102E]" data-testid="invite-whitelabel-check" />
            <span className="text-xs text-ink-700"><span className="font-bold text-ink">White-label the flyer</span> — hides the &ldquo;Powered by Ask Redline&rdquo; line in the footer</span>
          </label>
        </div>

        {/* Per-community wording tweaks — live preview below, optional save for next time */}
        <div data-testid="invite-customize" className="rounded-xl border border-sand-200 bg-sand-50 px-3 py-2.5 space-y-2">
          <div className="text-[11px] uppercase tracking-wide font-bold text-ink/50">Customize this flyer</div>
          <input data-testid="invite-headline-input" value={headline} maxLength={80}
            onChange={(e) => setHeadline(e.target.value)} placeholder={`Headline — ${persona}`}
            className="w-full rounded-lg border border-sand-200 bg-white px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-amber-500/30" />
          <textarea data-testid="invite-welcome-input" value={welcome} rows={2} maxLength={220}
            onChange={(e) => setWelcome(e.target.value)} placeholder="Welcome line — leave blank for the standard wording"
            className="w-full rounded-lg border border-sand-200 bg-white px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-amber-500/30 resize-none" />
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-muted-foreground">Blank fields use the standard wording. Edits show in the preview instantly.</span>
            <button data-testid="invite-save-defaults" onClick={saveDefaults} disabled={saving}
              className="shrink-0 text-xs font-semibold rounded-full border border-sand-300 bg-white px-3 py-1.5 text-ink-700 hover:bg-sand-100 transition-colors disabled:opacity-50">
              {saving ? "Saving…" : "Save for next time"}
            </button>
          </div>
        </div>

        {/* On-screen preview */}
        <div id="invite-doc" data-testid="invite-flyer" className="rounded-2xl border-2 border-ink/10 bg-white px-8 py-8 overflow-hidden">
          <FlyerPage t={t} qrId="invite-qr-canvas" beta={beta} headline={headline} welcome={welcome} whiteLabel={whiteLabel} />
        </div>

        {/* Print-only copy, portaled to <body> so window.print() lays out a clean full page */}
        {createPortal(
          <div id="invite-doc-print"><FlyerPage t={t} qrId="invite-qr-canvas-print" testPrefix="print" beta={beta} headline={headline} welcome={welcome} whiteLabel={whiteLabel} /></div>,
          document.body
        )}

        <div className="grid grid-cols-2 gap-2 pt-1">
          <button data-testid="poster-print-btn" onClick={() => window.print()}
            className="rounded-full bg-ink text-white font-semibold py-2.5 text-sm flex items-center justify-center gap-1.5 hover:bg-ink-700 transition-colors">
            <Printer className="h-4 w-4" /> Print / Save PDF
          </button>
          <button data-testid="poster-download-btn" onClick={downloadPng}
            className="rounded-full bg-sand-100 text-ink font-semibold py-2.5 text-sm flex items-center justify-center gap-1.5 hover:bg-sand-200 transition-colors">
            <Download className="h-4 w-4" /> Download QR
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
